/**
 * Utility functions for formatting and display
 */

// Format number for display with K/M/B suffix
export function formatNumber(value) {
  if (!value || value === 0) return '0.00';

  const absValue = Math.abs(value);

  if (absValue >= 1e9) {
    return (value / 1e9).toFixed(2) + 'B';
  }
  if (absValue >= 1e6) {
    return (value / 1e6).toFixed(2) + 'M';
  }
  if (absValue >= 1e3) {
    return (value / 1e3).toFixed(2) + 'K';
  }

  return value.toFixed(2);
}

// Format price with appropriate decimal places
export function formatPrice(price) {
  if (!price || price === 0) return '$0.00';

  if (price < 1) {
    return `$${price.toFixed(6)}`;
  } else if (price < 100) {
    return `$${price.toFixed(4)}`;
  } else {
    return `$${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
}

// Format timestamp to relative time
export function formatTimestamp(timestamp) {
  const now = Date.now();
  const tradeTime = new Date(timestamp).getTime();
  const diffMs = now - tradeTime;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffMins < 60) {
    return diffMins <= 1 ? 'just now' : `${diffMins}m ago`;
  }

  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }

  const date = new Date(timestamp);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[date.getMonth()];
  const day = date.getDate();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${month} ${day}, ${hours}:${minutes}`;
}

// Play notification sound
export function playNotificationSound() {
  try {
    const audio = new Audio('/assets/bell-98033.mp3');
    audio.play().catch(error => {
      console.error('Error playing notification sound:', error);
    });
  } catch (error) {
    console.error('Error creating audio:', error);
  }
}

// Get exchange logo path
export function getExchangeLogo(exchange) {
  const logos = {
    'HYPERLIQUID': '/assets_new/img/HL symbol_white.svg',
    'BINANCE': '/assets_new/img/binance.svg',
    'BINANCE_FUTURES': '/assets_new/img/binance.svg',
    'BYBIT': '/assets_new/img/bybit.svg',
    'OKX': '/assets/img/okx@3x.png',
    'COINBASE': '/assets_new/img/coinbase.svg',
    'UPBIT': '/assets_new/img/upbit.svg'
  };
  return logos[exchange] || '/assets_new/img/binance.svg';
}

// R2 CDN base URL for coin icons
const R2_PUBLIC_URL = import.meta.env.VITE_R2_PUBLIC_URL || 'https://cdn.tradermap.io';

// Get coin icon URL from R2 CDN
export function getCoinIconUrl(symbol) {
  const normalizedSymbol = symbol.toLowerCase().replace(/usdt$|usd$/i, '');
  return `${R2_PUBLIC_URL}/icons/${normalizedSymbol}usdt.png`;
}

// Default coin icon fallback - visible gray circle with "?" on dark backgrounds
export const DEFAULT_COIN_ICON = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32'%3E%3Ccircle cx='16' cy='16' r='14' fill='%23444' stroke='%23666' stroke-width='1'/%3E%3Ctext x='16' y='21' text-anchor='middle' fill='%23aaa' font-size='14' font-family='sans-serif'%3E%3F%3C/text%3E%3C/svg%3E";
